import {useEffect, useState, useCallback, useRef} from '@wordpress/element';
import {isEmail} from '@wordpress/url';
import {CART_STORE_KEY, PAYMENT_STORE_KEY} from '@woocommerce/block-data';
import {STORE_KEY} from '@wc-braintree/block-data';
import {useDispatch, useSelect} from '@wordpress/data';
import {extractFullName} from "../../utils";

export const useProcessFastlane = (
    {
        i18n,
        email,
        onError,
        fastlane,
        buttonRef,
        shippingData,
        openFastlaneModal
    }) => {

    const [processing, setProcessing] = useState(false);
    const currentData = useRef({email, fastlane, buttonRef, onError, shippingData});

    const {
        emails,
        isCustomerFound
    } = useSelect(select => {
        const store = select(STORE_KEY);
        return {
            emails: store.getCanceledEmails(),
            isCustomerFound: store.isCustomerFound()
        }
    })

    const {
        setBillingAddress
    } = useDispatch(CART_STORE_KEY);

    const {
        setPaymentToken,
        addCanceledEmail,
        setCustomerContextId
    } = useDispatch(STORE_KEY);

    const {
        __internalSetActivePaymentMethod: setActivePaymentMethod
    } = useDispatch(PAYMENT_STORE_KEY);

    useEffect(() => {
        currentData.current = {
            ...currentData.current,
            email,
            onError,
            fastlane,
            buttonRef,
            shippingData
        }
    }, [email, onError, fastlane, buttonRef, shippingData]);

    const removeEmptyValues = (obj) => {
        return Object.fromEntries(
            Object.entries(obj).filter(([_, value]) =>
                value !== undefined && value !== null && value !== ''
            )
        );
    }

    const updateShippingAddress = useCallback((address) => {
        const {
            shippingData
        } = currentData.current;

        const {
            setShippingAddress
        } = shippingData;

        setShippingAddress(removeEmptyValues({
            first_name: address?.firstName || '',
            last_name: address?.lastName,
            address_1: address?.streetAddress,
            address_2: address?.extendedAddress,
            city: address?.locality || '',
            state: address?.region || '',
            postcode: address?.postalCode || '',
            country: address?.countryCodeAlpha2 || '',
            phone: address?.phoneNumber || ''
        }));
    }, []);

    const updateBillingAddress = useCallback((address) => {
        setBillingAddress(removeEmptyValues({
            first_name: address?.firstName || '',
            last_name: address?.lastName,
            address_1: address?.streetAddress,
            address_2: address?.extendedAddress,
            city: address?.locality || '',
            state: address?.region || '',
            postcode: address?.postalCode || '',
            country: address?.countryCodeAlpha2 || '',
            phone: address?.phoneNumber || ''
        }));
    }, [setBillingAddress]);

    const scrollToElement = useCallback((selector, offset = 100) => {
        const el = document.querySelector(selector);
        if (!el) {
            return;
        }
        const rect = el.getBoundingClientRect();
        const scrollPosition = rect.top + window.scrollY - offset;
        window.scrollTo({
            behavior: 'smooth',
            left: 0,
            top: scrollPosition
        });
    }, []);

    const triggerAuthentication = useCallback(async (email, showSignup = true) => {
        const {
            fastlane,
            shippingData,
        } = currentData.current;
        const {
            needsShipping
        } = shippingData;
        const {
            profile,
            identity
        } = fastlane;

        try {
            setProcessing(true);
            const {
                customerContextId
            } = await identity.lookupCustomerByEmail(email);

            if (customerContextId) {
                const {
                    profileData,
                    authenticationState
                } = await identity.triggerAuthenticationFlow(customerContextId);
                if (authenticationState === 'canceled') {
                    addCanceledEmail(email);
                    setCustomerContextId(null);
                } else if (authenticationState === 'succeeded') {
                    let {
                        name,
                        card,
                        shippingAddress
                    } = profileData;
                    setCustomerContextId(customerContextId);

                    if (needsShipping) {
                        const {
                            selectedAddress
                        } = await profile.showShippingAddressSelector();
                        if (!selectedAddress) {
                            addCanceledEmail(email);
                            setCustomerContextId(null);
                            return;
                        }
                        shippingAddress = selectedAddress;
                        updateShippingAddress(shippingAddress);
                    }

                    if (card?.paymentSource?.card?.billingAddress) {
                        updateBillingAddress({
                            ...name,
                            ...card.paymentSource.card.billingAddress,
                            phoneNumber: shippingAddress?.phoneNumber || null
                        });
                    }

                    setPaymentToken(card);
                    setActivePaymentMethod('braintree_cc');
                    scrollToElement('#radio-control-wc-payment-method-options-braintree_cc');
                }
            } else if (showSignup) {
                setCustomerContextId(null);
                // display the Fastlane modal
                const token = await openFastlaneModal();

                if (token) {
                    if (token?.paymentSource?.card?.billingAddress) {
                        let address = token.paymentSource.card.billingAddress;
                        if (token.paymentSource.card.billingAddress.toBtSdkType) {
                            address = token.paymentSource.card.billingAddress.toBtSdkType();
                        } else {
                            const name = extractFullName(token.paymentSource.card.name);
                            address = {
                                firstName: name[0],
                                lastName: name[1],
                                ...address
                            }
                            updateShippingAddress(address);
                        }
                        updateBillingAddress(address);
                    }

                    setPaymentToken(token);
                    setActivePaymentMethod('braintree_cc');
                    scrollToElement('#radio-control-wc-payment-method-options-braintree_cc');
                }
            }
        } catch (error) {
            console.info(error);
        } finally {
            setProcessing(false);
        }

    }, [
        scrollToElement,
        openFastlaneModal,
        updateBillingAddress,
        updateShippingAddress
    ]);

    const onClick = useCallback(async (e) => {
        e.preventDefault();
        const {
            email,
            onError
        } = currentData.current;

        if (!email) {
            return onError(i18n.email_empty);
        }
        if (!isEmail(email)) {
            return onError(i18n.email_invalid);
        }

        await triggerAuthentication(email);

    }, [
        scrollToElement,
        updateBillingAddress,
        updateShippingAddress,
        triggerAuthentication
    ]);

    return {
        emails,
        onClick,
        processing,
        isCustomerFound,
        triggerAuthentication
    }
}